import type {
  PurchaseTicketData,
  PurchaseUnavailableReason,
} from "modules/ticket";

/**
 * upgrade に必要な追加購入枚数が在庫を超えており、一括購入できない場合に true を返す。
 * backend が返す元の枚数は変更せず、表示・枚数選択に必要な在庫不足状態を frontend で導出する。
 */
export const isUpgradeTicketInventoryShortage = (
  ticket: PurchaseTicketData
): boolean =>
  ticket.requiredTicketType === "upgrade" &&
  ticket.purchaseRequiredTicketCount !== undefined &&
  ticket.purchaseRequiredTicketCount > ticket.remaining;

/**
 * API が返す購入可能枚数と条件付きチケット情報から、frontend で利用する購入不可理由を計算する。
 */
export const getTicketUnavailableReason = (
  ticket: PurchaseTicketData
): PurchaseUnavailableReason | undefined => {
  if (ticket.remaining === -1) {
    return "soldOut";
  }

  if (isUpgradeTicketInventoryShortage(ticket)) {
    return "insufficientStockForUpgrade";
  }

  if (ticket.remaining === 0) {
    return "purchaseLimitReached";
  }

  if (
    ticket.purchaseRequiredTicketCount !== undefined &&
    ticket.purchaseRequiredTicketCount <= 0
  ) {
    return "requiredTicketNotOwned";
  }

  return undefined;
};

const isActiveDiscount = (
  ticket: PurchaseTicketData
): ticket is PurchaseTicketData & { discountFrom: string } =>
  ticket.requiredTicketType === "discount" &&
  !!ticket.discountFrom &&
  (ticket.purchaseRequiredTicketCount ?? 0) > 0;

/**
 * このチケットが、有効な割引チケットの「割引もと」として参照されているか。
 * true のとき、割引が有効なユーザーには通常チケット(割引もと)を非表示にする。
 *
 * 割引有効 = requiredTicketType が "discount" で、discountFrom があり、
 * かつ前提チケットを所持している(purchaseRequiredTicketCount > 0)。
 */
export const isActiveDiscountOriginal = (
  ticket: PurchaseTicketData,
  tickets: PurchaseTicketData[]
): boolean =>
  tickets.some(
    (candidate) =>
      isActiveDiscount(candidate) && candidate.discountFrom === ticket._id
  );
